import join from 'url-join';
import qs from 'qs';
import Cache from './Cache';
import { destroy, get } from './dao/helpers/fetch';
import paramify from './dao/helpers/paramify';
import ConfigDAO from './dao/ConfigDAO';
import DAO from './dao/DAO';
import { SLOW_REQUEST_THRESHOLD } from './constants/urls';
import User from './models/User';
import Promise from 'bluebird';
import moment from 'moment';
import { DEFAULT_ERROR_MESSAGE } from './dao/helpers/getError';
import { v4 as uuid } from 'uuid';

const setConfig = (config) => {
  window.config = config;
};

export default class Session {
  dao = (null: ?DAO);
  user = (null: ?Object);
  validatedToken = (null: ?string);
  routeToPush = (null: ?Object);
  modalToPush = (null: ?string);
  errorToShow = (null: ?string);

  initialize = () => {
    // Get config data
    return (
      ConfigDAO.fetch()

        // Set config on the window so that we can access anywhere
        .then(setConfig)
        .then(this.processUrlHash) // Returns user access token from hash or localStorage, or null.
        .then((tokenInfo) => {
          if (tokenInfo) {
            return this.validateToken(tokenInfo);
          } else {
            const loginError = new Error(DEFAULT_ERROR_MESSAGE);
            return Promise.reject(loginError);
          }
        })
    );
  };

  getUser = () => {
    return this.isLoggedIn() ? this.user : null;
  };

  /**
   * Pro
   * @returns string - user access token.
   */
  processUrlHash = () => {
    const { hash } = window.location;
    const hashString =
      hash.length > 0 && hash[ 0 ] === '#' ? hash.substr(1) : hash;
    const hashParams = qs.parse(hashString) || {};
    const hasToken = hashParams && hashParams.access_token;

    // Always look at the url for a token first, then return whatever is is in the cache
    const token = hasToken ? hashParams.access_token : Cache.getAccessToken();

    if (hasToken) {
      window.history.replaceState(
        {},
        window.document.title,
        window.location.pathname
      );
    }

    return token
      ? {
        token,
        expires: moment().add(parseInt(hashParams.expires_in), 's').valueOf(),
      }
      : null;
  };

  hasRouteToPush = () => !!this.routeToPush;
  popRouteToPush = () => {
    const poppedRoute = { ...this.routeToPush };
    this.routeToPush = null;
    return poppedRoute;
  };

  popModalToPush = () => {
    const poppedModal = this.modalToPush;
    this.modalToPush = null;
    return poppedModal;
  };

  hasActionToPush = () => !!this.actionToPush;

  hasErrorToShow = () => !!this.errorToShow;
  popErrorToShow = () => {
    const poppedError = this.errorToShow;
    this.errorToShow = null;
    return poppedError;
  };

  getValidatedToken = () => this.validatedToken;
  isLoggedIn = () => Boolean(this.getValidatedToken());

  login = (saveReturnHref = true) => {
    const fmIdParams = paramify({
      redirect_url: window.location.origin,
      client_id_fms: window.config.ADMIN_PORTAL_WEB_CLIENT_ID,
    });

    const hudlIdParams = paramify({
      client_id: window.config.HUDL_CLIENT_ID_ACCOUNT,
      redirect_uri:
        join(window.config.IDENTITY_SERVER_URL, 'callback/hudl-auth?') +
        fmIdParams,
      response_type: 'code id_token',
      scope: 'openid offline_access profile email',
      response_mode: 'form_post',
      nonce: uuid(),
    });

    if (saveReturnHref) {
      Cache.saveUserRequestedHref(window.location.hash.slice(1));
    }

    Cache.clearToken();
    window.location.replace(
      join(window.config.HUDL_IDENTITY_URL, 'authorize?') + hudlIdParams
    );
  };

  logout = () => {
    Cache.clearUserRequestedHref();
    const at = Cache.getAccessToken();
    if (at) {
      this.logoutID(at);
    } else {
      this.deleteCookie();
    }
  };

  onValidLogin = (token: Object, startTimestamp: Number) => {
    this.setValidatedToken(token);
    // We will access the DAO through the session, because session info is contained in each request
    this.setDAO(
      new DAO({
        accessToken: this.processUrlHash().token,
        statsUrl: window.config.FASTSTATS_API_URL,
        fastServerUrl: window.config.FAST_SERVER_URL,
        fastDrawUrl: window.config.FAST_DRAW_URL,
        servletUrl: window.config.SERVLET_URL,
        mediaServerUrl: window.config.MEDIA_SERVER_URL,
        fastRecruitUrl: window.config.FASTRECRUIT_URL,
        fastAccountUrl: window.config.FASTACCOUNT_API_URL,
        adminPortalIdentityUrl: window.config.ADMIN_PORTAL_IDENTITY_SERVER_URL,
      })
    );
    this.setUser(token);

    // Check and log any login longer than 4 seconds.
    const loginTime = Date.now() - startTimestamp;

    if (loginTime >= SLOW_REQUEST_THRESHOLD) {
      const logger = this.dao.getLogger();
      logger.log(
        `Authorization for userId:${token.userDetails.userId}  took ${loginTime}ms`
      );
      logger.send();
    }

    return this;
  };

  setDAO = (dao: DAO) => {
    this.dao = dao;
  };

  setValidatedToken = (token: Object) => {
    Cache.setAccessToken(token.access_token);
    Cache.setTokenExpiration(token.expires);
    this.validatedToken = token;
    return token;
  };

  setUser = (token: Object) => {
    this.user = User.build(token);
  };

  validateToken = (tokenInfo: Object) => {
    const url = join(
      window.config.ADMIN_PORTAL_IDENTITY_SERVER_URL,
      'token',
      tokenInfo.token
    );
    const clientId = window.config.ADMIN_PORTAL_WEB_CLIENT_ID;
    const clientSecret = window.config.ADMIN_PORTAL_WEB_CLIENT_SECRET;
    const headers = {
      Authorization: `Basic ${window.btoa(`${clientId}:${clientSecret}`)}`,
    };

    return get({ headers, url })
      .then((res) => {
        return { ...tokenInfo, ...res };
      })
      .finally(() => {
        // Always set the access token in cache so logout works properly.
        Cache.setAccessToken(tokenInfo.token);
      });
  };

  logoutID = (tokenInfo: ?string) => {
    const url = join(
      window.config.ADMIN_PORTAL_IDENTITY_SERVER_URL,
      'logout',
      tokenInfo
    );
    const clientId = window.config.ADMIN_PORTAL_WEB_CLIENT_ID;
    const clientSecret = window.config.ADMIN_PORTAL_WEB_CLIENT_SECRET;
    const headers = {
      Authorization: `Basic ${window.btoa(`${clientId}:${clientSecret}`)}`,
    };

    return get({ headers, url }).then((res) => {
      this.login(false);
    });
  };

  deleteCookie() {
    const url = join(window.config.ADMIN_PORTAL_IDENTITY_SERVER_URL, 'cookie');
    const clientId = window.config.ADMIN_PORTAL_WEB_CLIENT_ID;
    const clientSecret = window.config.ADMIN_PORTAL_WEB_CLIENT_SECRET;
    const headers = {
      Authorization: `Basic ${window.btoa(`${clientId}:${clientSecret}`)}`,
    };

    return destroy({ headers, url }).then(() => {
      this.login(false);
    });
  }
}
